import { observable, set } from 'mobx';
import { IBaseFlowContext } from '../../types/builder.types';
import { FlowAction } from './flow-action';
import {
    calculatePageBaseDataForServer,
    CommandsRelation,
    mergeCommands
} from '../../utils/commands.utils';
import { Action, IActionDataForServer, IBaseAction } from './action';
import type { RootStore } from '~/stores/root.store';

export interface IFlowActionGroupContext extends IBaseFlowContext {
    blockId?: string;
}

export interface IFlowActionGroup
    extends Omit<
        IBaseAction<IFlowActionGroupContext>,
        'ctx' | 'createApplyDataForServer' | 'createRevertDataForServer'
    > {
    createApplyDataForServer?: () => IActionDataForServer;
    createRevertDataForServer?: () => IActionDataForServer;
    groupedActions?: Array<Action>;
    ctx?: IFlowActionGroupContext;

    groupActions: () => void;
}

export class FlowActionGroup
    extends FlowAction<IFlowActionGroupContext>
    implements IFlowActionGroup
{
    @observable groupedActions: Array<Action>;
    wasAppliedSideEffectOverride: boolean = false;

    constructor(rootStore: RootStore) {
        super(rootStore);

        this.groupedActions = [];
        this.ctx = {
            flow: null,
            commands: [],
            revertCommands: []
        };

        this.applySideEffects = () =>
            this.callActionsCallback('applySideEffects');
        this.revertSideEffects = () =>
            this.callActionsCallback('revertSideEffects');
        this.createApplyDataForServer = () =>
            calculatePageBaseDataForServer(this.ctx.commands, this);
        this.createRevertDataForServer = () =>
            calculatePageBaseDataForServer(this.ctx.revertCommands, this);
    }

    initActionGroup({
        applySideEffects,
        revertSideEffects,
        ...actionData
    }: IFlowActionGroup) {
        set(this, actionData);
        this.applySideEffects = () => {
            this.callActionsCallback('applySideEffects');
            applySideEffects?.();
        };
        this.revertSideEffects = () => {
            this.callActionsCallback('revertSideEffects');
            revertSideEffects?.();
        };

        if (applySideEffects) {
            this.wasAppliedSideEffectOverride = true;
        }
    }

    setContext<TContext extends IBaseFlowContext>(ctx: Partial<TContext>) {
        const { commands, revertCommands, ...restOfCtx } = ctx;

        super.setContext(<any>restOfCtx);

        if (!commands && !revertCommands) {
            return;
        }

        this.ctx.commands = mergeCommands(
            commands,
            this.ctx.commands,
            CommandsRelation.accumulate
        );
        this.ctx.revertCommands = mergeCommands(
            revertCommands,
            this.ctx.revertCommands,
            CommandsRelation.accumulate
        );
    }

    groupActions() {}

    addAction<TContext extends IBaseFlowContext>(action: Action<TContext>) {
        this.setContext({
            ...action.ctx,
            flow: this.ctx.flow || action.ctx.flow
        });

        action.dataForServer.apply = calculatePageBaseDataForServer(
            action.ctx.commands,
            action
        );

        this.groupedActions.push(action);
    }

    revertCommands() {
        // This case can happen when the first action in the group actions fails so no actions were grouped yet.
        // The failed action will revert itself.
        if (!this.ctx.revertCommands?.length) {
            return;
        }

        super.revertCommands();
    }

    actionAs<T extends FlowAction<IBaseFlowContext>>() {
        return this as unknown as T;
    }

    private callActionsCallback(callbackName: string, data: any = null) {
        this.groupedActions.forEach(action => {
            if (!action[callbackName]) {
                return;
            }

            action[callbackName](data);
        });
    }
}
